import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  addDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import Layout from '@components/Layout';
import MarketplaceMenu from '@components/MarketplaceMenu';
import { toast } from 'react-hot-toast';
import { db } from '@root/firebase';
import dayjs from 'dayjs';
import Comments from '@components/Comments';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';
import GetViews from '@components/GetViews';
import Modal from '@components/Modal';
import StarRating from '@components/StarRating';
import { logMarketplaceClick } from '@/utils/referrals';
import { recordViewOnce } from '@/components/recordViewOnce';
import VerifiedBadge from '@/components/VerifiedBadge';
import starEmpty from '@public/images/star_0.svg';
import starHalf from '@public/images/star_half.png';
import starFilled from '@public/images/star_1.svg';

const PAGE_SIZE = 6;

function Stars({ value = 0, size = 18, gap = 2 }) {
  // value like 3.5 → [full, full, full, half, empty]
  const stars = Array.from({ length: 5 }, (_, i) => {
    const s = i + 1;
    if (value >= s) return starFilled;
    if (value >= s - 0.5) return starHalf;
    return starEmpty;
  });
  return (
    <div className='d-inline-flex align-items-center' style={{ gap }}>
      {stars.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`star-${i}`}
          style={{ width: size, height: size, userSelect: 'none' }}
          draggable={false}
        />
      ))}
    </div>
  );
}

/** Build a parent→children tree and roll up counts from items array */
function buildTreeWithCounts(categories, items) {
  // Count items per subCategoryId
  const counts = new Map();
  (items || []).forEach((it) => {
    const sid = it?.subCategoryId;
    if (!sid) return;
    counts.set(sid, (counts.get(sid) || 0) + 1);
  });

  // Clone + seed counts
  const byId = new Map(
    (categories || []).map((c) => [
      c.id,
      { ...c, children: [], totalCount: counts.get(c.id) || 0 },
    ])
  );

  // Link children
  for (const c of byId.values()) {
    if (c.parent && byId.has(c.parent)) byId.get(c.parent).children.push(c);
  }

  // Roll up child counts to parents
  const roots = [...byId.values()].filter((c) => !c.parent);
  const roll = (node) => {
    let sum = node.totalCount;
    node.children.forEach((ch) => (sum += roll(ch)));
    node.totalCount = sum;
    return sum;
  };
  roots.forEach(roll);
  return roots;
}

function Marketplace() {
  const { globalUser, globalData, defaultAvatarUrl } = useAuth();

  // Tabs: 'all' | 'adverts' | 'products'
  const [tab, setTab] = useState('all');

  // Category data (left menu)
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);

  // Active category filters
  const [activeParentId, setActiveParentId] = useState(null);
  const [activeSubCategoryId, setActiveSubCategoryId] = useState(null);

  // Feeds + cursors + flags
  const [advertsFeed, setAdvertsFeed] = useState([]);
  const [productsFeed, setProductsFeed] = useState([]);
  const [advertsCursor, setAdvertsCursor] = useState(null);
  const [productsCursor, setProductsCursor] = useState(null);
  const [advertsHasMore, setAdvertsHasMore] = useState(true);
  const [productsHasMore, setProductsHasMore] = useState(true);

  const [isSaving, setIsSaving] = useState(false);

  // Separate loading flags per feed
  const [loadingAdverts, setLoadingAdverts] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // --- NEW: keep the latest snapshots to avoid stale closures when rebuilding the tree
  const catsRef = useRef([]); // latest categories (flat)
  const itemsRef = useRef([]); // latest marketplace items (adverts + products)

  const handleReferralClick = async (e, item) => {
    try {
      // Optional: prevent default only if you want to delay open slightly
      // e.preventDefault();

      await logMarketplaceClick(item.id, globalUser?.uid, {
        product: item.product || item.title,
        externalUrl: item.externalUrl || null,
      });

      // Optionally open manually to ensure event logs first
      // window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error logging referral click:', err);
    }
  };

  // --- JOINED REALTIME LISTENERS: categories + items (keeps menu counts live without disappearing)
  useEffect(() => {
    // Categories listener
    const unsubCats = onSnapshot(
      collection(db, 'marketplaceCategories'),
      (catSnap) => {
        const cats = catSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name || data.title || '(unnamed)',
            parent: data.parent ?? data.parentId ?? null,
          };
        });
        catsRef.current = cats; // save latest
        setFlatCategories(cats); // expose flat for filters
        setCategoriesTree(
          buildTreeWithCounts(catsRef.current, itemsRef.current)
        );
      },
      (err) => {
        console.error('Categories live error:', err);
        toast.error('Failed to load categories.');
      }
    );

    // Items listener (both adverts & products)
    const unsubItems = onSnapshot(
      collection(db, 'marketplace'),
      (itemSnap) => {
        const items = itemSnap.docs.map((d) => d.data());
        itemsRef.current = items; // save latest
        setCategoriesTree(
          buildTreeWithCounts(catsRef.current, itemsRef.current)
        );
      },
      (err) => {
        console.error('Marketplace live error:', err);
        toast.error('Failed to load items.');
      }
    );

    return () => {
      unsubCats();
      unsubItems();
    };
  }, []);

  // ------------------------------------------------------------------
  // Category filter constraints for queries
  // ------------------------------------------------------------------
  const categoryWhereConstraints = useMemo(() => {
    if (activeSubCategoryId)
      return [where('subCategoryId', '==', activeSubCategoryId)];

    if (activeParentId) {
      const childIds = flatCategories
        .filter((c) => c.parent === activeParentId)
        .map((c) => c.id);
      if (childIds.length === 0)
        return [where('subCategoryId', '==', '__none__')];
      if (childIds.length <= 10)
        return [where('subCategoryId', 'in', childIds)];
      // >10 children → client-side filter fallback
      return [{ __clientFilterChildren: childIds }];
    }
    return [];
  }, [activeParentId, activeSubCategoryId, flatCategories]);

  // ------------------------------------------------------------------
  // Build query for adverts/products
  // ------------------------------------------------------------------
  const buildQuery = useCallback(
    (kind, afterDoc = null) => {
      const base = collection(db, 'marketplace');
      const clauses = [where('type', '==', kind)];

      let needsClientFilter = false;
      let clientChildren = null;

      if (categoryWhereConstraints.length) {
        const maybe = categoryWhereConstraints[0]?.__clientFilterChildren;
        if (Array.isArray(maybe)) {
          needsClientFilter = true;
          clientChildren = maybe;
        } else {
          clauses.push(...categoryWhereConstraints);
        }
      }

      if (kind === 'adverts') {
        clauses.push(where('startDate', '!=', null));
        clauses.push(orderBy('startDate', 'asc'));
      } else {
        clauses.push(where('createdAt', '!=', null));
        clauses.push(orderBy('createdAt', 'desc'));
      }

      if (afterDoc) clauses.push(startAfter(afterDoc));
      clauses.push(limit(PAGE_SIZE));

      return { q: query(base, ...clauses), needsClientFilter, clientChildren };
    },
    [categoryWhereConstraints]
  );

  // ------------------------------------------------------------------
  // Fetch a page (paginates; works alongside the live “first page”)
  // ------------------------------------------------------------------
  const fetchPage = useCallback(
    async (kind, initial = false) => {
      const isAdverts = kind === 'adverts';
      const setLoading = isAdverts ? setLoadingAdverts : setLoadingProducts;
      const loading = isAdverts ? loadingAdverts : loadingProducts;
      const cursor = isAdverts ? advertsCursor : productsCursor;
      const hasMore = isAdverts ? advertsHasMore : productsHasMore;

      if (loading || (!initial && !hasMore)) return;

      setLoading(true);
      try {
        let rows = [];
        let lastDoc = cursor;
        let pagesTried = 0;
        const MAX_PAGES_THIS_CALL = 3;

        while (rows.length === 0 && pagesTried < MAX_PAGES_THIS_CALL) {
          const { q, needsClientFilter, clientChildren } = buildQuery(
            kind,
            initial && pagesTried === 0 ? null : lastDoc
          );
          const snap = await getDocs(q);
          if (snap.empty) {
            if (isAdverts) setAdvertsHasMore(false);
            else setProductsHasMore(false);
            break;
          }

          let batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          if (needsClientFilter && Array.isArray(clientChildren)) {
            batch = batch.filter((r) =>
              clientChildren.includes(r.subCategoryId || '')
            );
          }

          if (isAdverts) {
            batch = batch.filter((a) => {
              const start = a.startDate?.seconds
                ? dayjs.unix(a.startDate.seconds)
                : null;
              const end = a.endDate?.seconds
                ? dayjs.unix(a.endDate.seconds)
                : null;
              if (!start) return false;
              return (
                dayjs().isSame(start, 'day') ||
                (dayjs().isAfter(start, 'day') &&
                  (!end || dayjs().isBefore(end, 'day')))
              );
            });
          }

          rows = batch;
          lastDoc = snap.docs[snap.docs.length - 1];

          if (rows.length === 0 && snap.size === PAGE_SIZE) {
            pagesTried += 1;
            continue;
          }

          if (isAdverts) {
            setAdvertsCursor(lastDoc);
            if (snap.size < PAGE_SIZE) setAdvertsHasMore(false);
          } else {
            setProductsCursor(lastDoc);
            if (snap.size < PAGE_SIZE) setProductsHasMore(false);
          }
          break;
        }

        if (rows.length) {
          if (isAdverts) {
            setAdvertsFeed((prev) => [
              ...prev,
              ...rows.filter((r) => !prev.some((p) => p.id === r.id)),
            ]);
          } else {
            setProductsFeed((prev) => [
              ...prev,
              ...rows.filter((r) => !prev.some((p) => p.id === r.id)),
            ]);
          }
        }
      } catch (err) {
        console.error('Fetch error:', err);
        if (isAdverts) setAdvertsHasMore(false);
        else setProductsHasMore(false);

        if (err?.code === 'failed-precondition') {
          toast.error(
            'Firestore index required. Open the console link to create it.'
          );
        } else if (err?.code === 'invalid-argument') {
          toast.error('Some items are missing the field used for ordering.');
        } else {
          toast.error('Failed to load items.');
        }
      } finally {
        setLoading(false);
      }
    },
    [
      advertsCursor,
      productsCursor,
      advertsHasMore,
      productsHasMore,
      loadingAdverts,
      loadingProducts,
      buildQuery,
    ]
  );

  // ------------------------------------------------------------------
  // Live “first page” per tab (keeps list fresh while user stays)
  // ------------------------------------------------------------------
  useEffect(() => {
    const unsubs = [];

    const attachLive = (kind) => {
      const base = collection(db, 'marketplace');
      const clauses = [where('type', '==', kind)];

      // apply category filters (when feasible)
      if (activeSubCategoryId) {
        clauses.push(where('subCategoryId', '==', activeSubCategoryId));
      } else if (activeParentId) {
        const childIds = flatCategories
          .filter((c) => c.parent === activeParentId)
          .map((c) => c.id);
        if (childIds.length && childIds.length <= 10) {
          clauses.push(where('subCategoryId', 'in', childIds));
        }
      }

      if (kind === 'adverts') {
        clauses.push(
          where('startDate', '!=', null),
          orderBy('startDate', 'asc'),
          limit(PAGE_SIZE)
        );
      } else {
        clauses.push(
          where('createdAt', '!=', null),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      }

      const qLive = query(base, ...clauses);
      const unsub = onSnapshot(qLive, (snap) => {
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (kind === 'adverts') {
          rows = rows.filter((a) => {
            const start = a.startDate?.seconds
              ? dayjs.unix(a.startDate.seconds)
              : null;
            const end = a.endDate?.seconds
              ? dayjs.unix(a.endDate.seconds)
              : null;
            if (!start) return false;
            return (
              dayjs().isSame(start, 'day') ||
              (dayjs().isAfter(start, 'day') &&
                (!end || dayjs().isBefore(end, 'day')))
            );
          });
          setAdvertsFeed(rows);
          setAdvertsCursor(snap.docs[snap.docs.length - 1] || null);
          setAdvertsHasMore(snap.size === PAGE_SIZE);
        } else {
          setProductsFeed(rows);
          setProductsCursor(snap.docs[snap.docs.length - 1] || null);
          setProductsHasMore(snap.size === PAGE_SIZE);
        }
      });
      unsubs.push(unsub);
    };

    // reset feeds on tab / category change, then attach live listeners
    setAdvertsFeed([]);
    setProductsFeed([]);
    setAdvertsCursor(null);
    setProductsCursor(null);
    setAdvertsHasMore(true);
    setProductsHasMore(true);

    if (tab === 'adverts') attachLive('adverts');
    else if (tab === 'products') attachLive('products');
    else {
      attachLive('adverts');
      attachLive('products');
    }

    return () => unsubs.forEach((u) => u());
  }, [tab, activeParentId, activeSubCategoryId, flatCategories]);

  // ------------------------------------------------------------------
  // Merge feeds for the "All" tab
  // ------------------------------------------------------------------
  const allItems = useMemo(() => {
    const normalized = [
      ...advertsFeed.map((x) => ({
        ...x,
        _sortAt: x.startDate?.seconds
          ? dayjs.unix(x.startDate.seconds).valueOf()
          : 0,
      })),
      ...productsFeed.map((x) => ({
        ...x,
        _sortAt: x.createdAt?.seconds
          ? dayjs.unix(x.createdAt.seconds).valueOf()
          : 0,
      })),
    ];
    return normalized.sort((a, b) => b._sortAt - a._sortAt);
  }, [advertsFeed, productsFeed]);

  // Infinite scroll: fetch additional pages beyond the live “first page”
  useEffect(() => {
    const onScroll = () => {
      const nearBottom =
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100;
      if (!nearBottom) return;

      if (tab === 'adverts') {
        if (!loadingAdverts && advertsHasMore) fetchPage('adverts');
      } else if (tab === 'products') {
        if (!loadingProducts && productsHasMore) fetchPage('products');
      } else {
        if (!loadingAdverts && advertsHasMore) fetchPage('adverts');
        if (!loadingProducts && productsHasMore) fetchPage('products');
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [
    tab,
    loadingAdverts,
    loadingProducts,
    advertsHasMore,
    productsHasMore,
    fetchPage,
  ]);

  // ------------------------------------------------------------------
  // Cards (keep your styling/layout exactly as you wanted)
  // ------------------------------------------------------------------
  const AdvertCard = ({ item }) => (
    <div className='col-md-6 col-xl-12 mb-4'>
      <div className='card overflow-hidden'>
        {item.image && (
          <img
            className='card-img-top'
            src={item.image}
            alt={item.title || 'Advert'}
          />
        )}
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-center'>
            <h5 className='card-title mb-0'>{item.title || 'Advert'}</h5>
            <div className='text-end ms-3'>
              {/* View Product */}
              <div
                className='tooltip-container'
                style={{ display: 'inline-block', marginRight: '10px' }}
              >
                <Link
                  to={`/app/marketplace/${item.id}`}
                  className='reaction-btn reaction-action'
                  aria-label={`View ${item.product || 'Product'}`}
                  title={`View ${item.product || 'Product'}`}
                  onClick={() => {
                    // fire-and-forget; the detail page will also attempt to record but dedupe will prevent double count
                    void recordViewOnce({
                      docPath: `marketplace/${item.id}`,
                      userId: globalUser?.uid,
                    });
                  }}
                >
                  <i className='fas fa-search reaction-icon' />
                </Link>
                <span className='tooltip'>View {item.product}</span>
              </div>
              {/* Like Product */}
              <GetViews docPath={`marketplace/${item.id}`} />
              {/* Like Product */}
              <LikeButton
                collectionPath='marketplace'
                documentId={item.id}
                userId={globalData.uid}
              />
              {/* Fav Product */}
              <FavoriteButton
                collectionPath='marketplace'
                documentId={item.id}
                userId={globalData.uid}
              />
            </div>
          </div>

          {item.description && (
            <div className='mt-2'>
              <ReadMoreTextBase
                text={item.description}
                wordLimit={3}
                readMore
              />
            </div>
          )}

          <div className='mt-2'>
            <small className='text-muted'>
              Listed:{' '}
              {item.startDate?.seconds
                ? dayjs.unix(item.startDate.seconds).format('DD MMM YYYY')
                : 'Unknown'}
            </small>
          </div>
        </div>

        <div className='card-footer bg-transparent border-top'>
          <Comments
            parentId={item.id}
            collectionPath='marketplace'
            globalUser={globalUser}
            defaultAvatarUrl={defaultAvatarUrl}
            parentIdNull={true}
          />
        </div>
      </div>
    </div>
  );

  const ProductCard = ({ item }) => {
    // live aggregates from the product doc
    const [ratingAvg, setRatingAvg] = useState(item.ratingAvg || 0);
    const [ratingCount, setRatingCount] = useState(item.ratingCount || 0);

    // reviews drawer
    const [showReviews, setShowReviews] = useState(false);
    const [reviews, setReviews] = useState([]);

    // modal state
    const [modalRating, setModalRating] = useState(0);
    const [modalText, setModalText] = useState('');
    const [savingReview, setSavingReview] = useState(false);

    // ---- live listen to product doc for avg/count
    useEffect(() => {
      const ref = doc(db, 'marketplace', item.id);
      const unsub = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() || {};
        setRatingAvg(Number(d.ratingAvg || 0));
        setRatingCount(Number(d.ratingCount || 0));
      });
      return () => unsub();
    }, [item.id]);

    // ---- open/close reviews: live listen to reviews subcollection
    useEffect(() => {
      if (!showReviews) return;
      const ref = collection(db, 'marketplace', item.id, 'reviews');
      const q = query(ref, orderBy('createdAt', 'desc'));
      const unsub = onSnapshot(q, (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReviews(rows);
      });
      return () => unsub();
    }, [showReviews, item.id]);

    // ---- save review + update aggregates atomically
    const handleSaveReview = async () => {
      if (!globalData?.uid) {
        toast.error('Global Data ID Empty.');
        return;
      }
      if (!modalRating || modalRating < 0.5) {
        toast.error('Please select a rating.');
        return;
      }
      setSavingReview(true);
      try {
        // 1) add review document
        const revRef = collection(db, 'marketplace', item.id, 'reviews');
        await addDoc(revRef, {
          uid: globalData.uid,
          rating: modalRating,
          review: (modalText || '').trim(),
          createdAt: serverTimestamp(),
        });

        // 2) update product aggregates (count/sum/avg) in a transaction
        const prodRef = doc(db, 'marketplace', item.id);
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(prodRef);
          const cur = snap.exists() ? snap.data() : {};
          const prevCount = Number(cur.ratingCount || 0);
          const prevSum = Number(cur.ratingSum || 0);
          const newCount = prevCount + 1;
          const newSum = prevSum + modalRating;
          const newAvg = newSum / newCount;
          tx.set(
            prodRef,
            { ratingCount: newCount, ratingSum: newSum, ratingAvg: newAvg },
            { merge: true }
          );
        });

        toast.success('Review submitted!');
        setModalRating(0);
        setModalText('');
      } catch (e) {
        console.error('Save review failed:', e);
        toast.error('Failed to submit review.');
      } finally {
        setSavingReview(false);
      }
    };

    return (
      <div className='col-md-6 col-xl-12 mb-4'>
        <div className='card h-100'>
          <div className='card-body'>
            <div className='row m-b-30'>
              {/* Image */}
              <div className='col-md-5 col-xxl-12'>
                <div className='new-arrival-product mb-4 mb-xxl-4 mb-md-0'>
                  <div className='new-arrivals-img-contnent'>
                    <img
                      className='img-fluid rounded'
                      src={item.avatar}
                      alt={item.product || 'Product'}
                    />
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className='col-md-7 col-xxl-12'>
                <div className='new-arrival-content position-relative'>
                  {/* Header: left = title/rating, right = price/likes */}
                  <div className='d-flex justify-content-between align-items-start flex-wrap'>
                    {/* Left side — Title + Stars */}
                    <div className='flex-grow-1'>
                      <div className='d-flex align-items-center justify-content-between mb-1'>
                        {/* Left: product name */}
                        <h4 className='mb-0 text-start flex-grow-1'>
                          <a
                            href={item.externalUrl || '#'}
                            target={item.externalUrl ? '_blank' : undefined}
                            rel={
                              item.externalUrl
                                ? 'noopener noreferrer'
                                : undefined
                            }
                            className='text-black text-decoration-none'
                            onClick={(e) => handleReferralClick(e, item)}
                          >
                            {item.product || item.title || 'Product'}
                          </a>
                        </h4>

                        {/* Right: verified badge */}
                        <div className='ms-2'>
                          <VerifiedBadge productId={item.id} compact />
                        </div>
                      </div>
                      {/* dynamic stars + review count */}
                      <div className='text-start'>
                        <Stars value={ratingAvg} />
                        <small className='review-text text-muted ms-2'>
                          (
                          <button
                            type='button'
                            className='btn btn-link p-0 align-baseline text-muted fs-12'
                            onClick={() => setShowReviews((s) => !s)}
                          >
                            {ratingCount}{' '}
                            {ratingCount === 1 ? 'review' : 'reviews'}
                          </button>
                          ) /{' '}
                          <button
                            type='button'
                            className='btn btn-link p-0 align-baseline text-muted fs-12'
                            data-bs-toggle='modal'
                            data-bs-target={`#writeReview-${item.id}`}
                          >
                            Write a Review
                          </button>
                        </small>
                      </div>

                      <div className='text-end'>
                        <p className='mb-1 fs-20 fw-bold text-primary fs-5'>
                          $
                          {typeof item.price === 'number'
                            ? item.price.toFixed(2)
                            : item.price || ''}
                        </p>
                      </div>
                    </div>

                    {/* Write a Review */}
                    <Modal
                      id={`writeReview-${item.id}`}
                      title='Write a Review'
                      onConfirm={handleSaveReview}
                      confirmText='Save'
                      loading={savingReview}
                      loadingText='Saving...'
                    >
                      <div className='mb-3 text-center'>
                        <div className='rating-widget mb-2 d-inline-flex align-items-center gap-2'>
                          <StarRating onRate={(v) => setModalRating(v)} />
                          <span className='text-muted small'>
                            {modalRating
                              ? `${
                                  Number.isInteger(modalRating)
                                    ? modalRating
                                    : modalRating.toFixed(1)
                                }/5`
                              : ''}
                          </span>
                        </div>
                        <div className='mb-3'>
                          <textarea
                            className='form-control'
                            placeholder='Share your thoughts...'
                            rows='5'
                            value={modalText}
                            onChange={(e) => setModalText(e.target.value)}
                            maxLength={2000}
                          />
                          <div className='form-text text-end'>
                            {modalText.length}/2000
                          </div>
                        </div>
                      </div>
                    </Modal>
                  </div>

                  {/* Meta info */}
                  <p className='mb-1 fs-12'>
                    Category:{' '}
                    <span className='item'>
                      {item.categoryName}
                      {item.subCategoryName ? ` - ${item.subCategoryName}` : ''}
                    </span>
                  </p>

                  {item.productCode && (
                    <p className='mb-1 fs-12'>
                      Product code:{' '}
                      <span className='item'>{item.productCode}</span>
                    </p>
                  )}

                  {item.brand && (
                    <p className='mb-1 fs-12'>
                      Brand: <span className='item'>{item.brand}</span>
                    </p>
                  )}

                  {item.description && (
                    <ReadMoreTextBase
                      text={item.description}
                      wordLimit={3}
                      readMore
                    />
                  )}

                  {/* Right side — Price + Like/Favorite */}
                  <div className='text-end ms-3'>
                    <div className='d-flex justify-content-end mt-2'>
                      {/* View Product */}
                      <div
                        className='tooltip-container'
                        style={{ display: 'inline-block', marginRight: '10px' }}
                      >
                        <Link
                          to={`/app/marketplace/${item.id}`}
                          className='reaction-btn reaction-action'
                          aria-label={`View ${item.product || 'Product'}`}
                          title={`View ${item.product || 'Product'}`}
                          onClick={() => {
                            // fire-and-forget; the detail page will also attempt to record but dedupe will prevent double count
                            void recordViewOnce({
                              docPath: `marketplace/${item.id}`,
                              userId: globalUser?.uid,
                            });
                          }}
                        >
                          <i className='fas fa-search reaction-icon' />
                        </Link>
                        <span className='tooltip'>View {item.product}</span>
                      </div>
                      {/* Like Product */}
                      <LikeButton
                        collectionPath='marketplace'
                        documentId={item.id}
                        userId={globalData?.uid}
                      />
                      {/* Favorite Product */}
                      <FavoriteButton
                        collectionPath='marketplace'
                        documentId={item.id}
                        userId={globalData?.uid}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews in the FOOTER */}
          <div className='card-footer bg-transparent border-top'>
            <div className='d-flex justify-content-between align-items-center'>
              <button
                className='btn btn-link p-0 fs-12'
                onClick={() => setShowReviews((s) => !s)}
              >
                {showReviews ? 'Hide reviews' : 'Show reviews'}
              </button>
              <small className='text-muted'>
                Avg:{' '}
                {ratingAvg
                  ? Number.isInteger(ratingAvg)
                    ? ratingAvg
                    : ratingAvg.toFixed(1)
                  : '—'}{' '}
                / 5
              </small>
            </div>

            {showReviews && (
              <ul className='list-unstyled mt-3 mb-0'>
                {reviews.length ? (
                  reviews.map((r) => (
                    <li key={r.id} className='border-top py-2'>
                      <div className='d-flex align-items-center justify-content-between'>
                        <Stars value={r.rating || 0} size={14} />
                        <small className='text-muted'>
                          {r.createdAt?.toDate?.()
                            ? dayjs(r.createdAt.toDate()).format(
                                'DD MMM YYYY, HH:mm'
                              )
                            : ''}
                        </small>
                      </div>
                      {r.review && <div className='fs-12 mt-1'>{r.review}</div>}
                    </li>
                  ))
                ) : (
                  <li className='text-muted'>No reviews yet.</li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFeed = () => {
    const grid = (nodes) => <div className='row'>{nodes}</div>;

    if (tab === 'adverts') {
      if (!advertsFeed.length && loadingAdverts) return <p>Loading adverts…</p>;
      if (!advertsFeed.length)
        return (
          <div className='col-sm-6 col-md-4'>
            <div className='alert alert-danger left-icon-big alert-dismissible fade show'>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='alert'
                aria-label='btn-close'
              >
                <span>
                  <i className='mdi mdi-btn-close'></i>
                </span>
              </button>
              <div className='media'>
                <div className='alert-left-icon-big'>
                  <span>
                    <i className='mdi mdi-help-circle-outline'></i>
                  </span>
                </div>
                <div className='media-body'>
                  <h5 className='mt-1 mb-2'>No Advertisments Yet!</h5>
                  <p className='mb-0'>
                    Hold tight advertisements are on there way!
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      return grid(
        advertsFeed.map((it) => <AdvertCard key={it.id} item={it} />)
      );
    }
    if (tab === 'products') {
      if (!productsFeed.length && loadingProducts)
        return <p>Loading products…</p>;
      if (!productsFeed.length)
        return (
          <div className='col-sm-6 col-md-12'>
            <div className='alert alert-danger left-icon-big alert-dismissible fade show'>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='alert'
                aria-label='btn-close'
              >
                <span>
                  <i className='mdi mdi-btn-close'></i>
                </span>
              </button>
              <div className='media'>
                <div className='alert-left-icon-big'>
                  <span>
                    <i className='mdi mdi-help-circle-outline'></i>
                  </span>
                </div>
                <div className='media-body'>
                  <h5 className='mt-1 mb-2'>No Products Yet!</h5>
                  <p className='mb-0'>
                    Hold tight new products are on there way!
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      return grid(
        productsFeed.map((it) => (
          <ProductCard key={it.id} item={it} globalData={globalData} />
        ))
      );
    }
    const isLoading = loadingAdverts || loadingProducts;
    if (!allItems.length && isLoading) return <p>Loading…</p>;
    if (!allItems.length) return <p>No items found.</p>;
    return grid(
      allItems.map((it) =>
        it.type === 'adverts' ? (
          <AdvertCard key={it.id} item={it} />
        ) : (
          <ProductCard key={it.id} item={it} globalData={globalData} />
        )
      )
    );
  };

  return (
    <Layout>
      {/* breadcrumbs */}
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Services</li>
            <li className='breadcrumb-item'>Marketplace</li>
          </ol>
        </div>
        <div className='col-lg-4' />
      </div>

      {/* Main Container Parent Size 12 */}
      <div className='col-xl-12 col-lg-4'>
        <div className='row align-items-start'>
          {/* Menu Container Child Size 3 */}
          <div className='col-xl-3 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Marketplace Categories</h5>
              </div>
              <div className='card-body'>
                <MarketplaceMenu
                  categories={categoriesTree}
                  onCategorySelect={(catId, isParent = false) => {
                    if (isParent) {
                      setActiveParentId(catId);
                      setActiveSubCategoryId(null);
                    } else {
                      setActiveSubCategoryId(catId);
                      setActiveParentId(null);
                    }
                  }}
                />
                {(activeParentId || activeSubCategoryId) && (
                  <button
                    className='btn btn-xs btn-outline-secondary mt-2'
                    onClick={() => {
                      setActiveParentId(null);
                      setActiveSubCategoryId(null);
                    }}
                  >
                    Clear category filter
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Center: Feed  Container Child Size 6 */}
          <div className='col-xl-6 col-lg-8'>
            {/* Tabs */}
            <ul className='nav nav-tabs mt-3 mb-3'>
              {['all', 'adverts', 'products'].map((t) => (
                <li className='nav-item' key={t}>
                  <button
                    className={`nav-link ${tab === t ? 'active' : ''}`}
                    onClick={() => setTab(t)}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                </li>
              ))}
            </ul>

            {renderFeed()}

            {/* Load more */}
            <div className='d-grid'>
              {tab === 'adverts' && advertsHasMore && (
                <button
                  className='btn btn-outline-primary'
                  disabled={loadingAdverts}
                  onClick={() => fetchPage('adverts')}
                >
                  {loadingAdverts ? 'Loading…' : 'Load more adverts'}
                </button>
              )}
              {tab === 'products' && productsHasMore && (
                <button
                  className='btn btn-outline-primary'
                  disabled={loadingProducts}
                  onClick={() => fetchPage('products')}
                >
                  {loadingProducts ? 'Loading…' : 'Load more products'}
                </button>
              )}
              {tab === 'all' && (advertsHasMore || productsHasMore) && (
                <button
                  className='btn btn-outline-primary'
                  disabled={loadingAdverts || loadingProducts}
                  onClick={() => {
                    if (advertsHasMore) fetchPage('adverts');
                    if (productsHasMore) fetchPage('products');
                  }}
                >
                  {loadingAdverts || loadingProducts ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </div>

          {/* Right: Notification Container Child Size 3 */}
          <div className='col-xl-3 col-lg-8'>
            <div className='card'>
              <div className='card-header'>
                <h5 className='mb-0'>Trending</h5>
              </div>
              <div className='card-body'>
                <p className='card-text'></p>
              </div>
              <div className='card-footer border-0 pt-0'>
                <p className='card-text d-inline'>Card footer</p>
                <a href='' className='card-link float-end'>
                  Card link
                </a>
              </div>
            </div>

            <div className='card'>
              <div className='card-header'>
                <h5 className='mb-0'>Featured</h5>
              </div>
              <div className='card-body'>
                <p className='card-text'></p>
              </div>
              <div className='card-footer border-0 pt-0'>
                <p className='card-text d-inline'>Card footer</p>
                <a href='' className='card-link float-end'>
                  Card link
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Marketplace;
